
import { mapActions } from "vuex";
import Vue from "vue";
import type { Vendor } from "@/services/OAuthService";
import OAuthService from "@/services/OAuthService";
import AuthorizationModal from "@/components/oauth2/AuthorizationModal.vue";
import PaymentProfileSync from "@/components/oauth2/PaymentProfileSync.vue";
import SettingsModal from "@/components/oauth2/SettingsModal.vue";

export default Vue.extend({
  name: "VendorList",
  components: {
    AuthorizationModal,
    PaymentProfileSync,
    SettingsModal,
  },
  props: {
    status: {
      type: String,
      default: null,
    },
    vendor: {
      type: String,
      default: null,
    },
    msg: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      headers: [
        { text: "Vendor", value: "displayName" },
        { text: "Status", value: "status", align: "center" },
        { text: "Actions", value: "actions", align: "center" },
      ],
      vendors: [] as Vendor[],
      showModal: false,
      selectedVendor: null as Vendor | null,
      centers: [] as Array<{ id: number; name: string }>,
      showSyncModal: false,
      syncingVendors: [] as string[],
      // Data for the Settings modal:
      showSettingsModal: false,
      // This object will hold the preset settings for the vendor.
      presetSettings: {} as Record<string, any>,
    };
  },
  async created() {
    if (this.status && this.vendor) {
      if (this.status === "success") {
        this.addSuccessMessage(`Authorization successful for ${this.vendor}`);
      } else if (this.status === "failed") {
        const errorMsg = this.msg
          ? this.msg
          : `Authorization failed for ${this.vendor}`;
        this.addErrorMessage(errorMsg);
      }
      this.$router.replace({ path: "/oauth2" });
    }
    await this.fetchVendors();
    await this.fetchCenters();
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    async fetchVendors() {
      try {
        const fetchedVendors = await OAuthService.getSupportedVendors();
        this.vendors = fetchedVendors.filter(
          (vendor): vendor is Vendor =>
            vendor !== null && typeof vendor.displayName === "string",
        );
        console.log("Fetched vendors:", this.vendors);
      } catch (error) {
        console.error("Failed to fetch vendors:", error);
        this.addErrorMessage("Failed to load vendors.");
      }
    },
    async fetchCenters() {
      this.centers = [
        { id: 1, name: "Center 1" },
        { id: 2, name: "Center 2" },
      ];
    },
    authorize(vendor: Vendor) {
      this.selectedVendor = vendor;
      this.showModal = true;
    },
    async revoke(vendor: Vendor) {
      try {
        await OAuthService.revokeAuthorization(vendor.vendor);
        const vendorIndex = this.vendors.findIndex(
          (v) => v.vendor === vendor.vendor,
        );
        if (vendorIndex !== -1) {
          Vue.set(this.vendors, vendorIndex, {
            ...this.vendors[vendorIndex],
            authorized: false,
          });
        }
        this.addSuccessMessage(
          `Revoked authorization for ${vendor.displayName}`,
        );
      } catch (error) {
        console.error("Failed to revoke authorization:", error);
        this.addErrorMessage(
          `Failed to revoke authorization for ${vendor.displayName}`,
        );
      }
    },
    async testConnection(vendor: Vendor) {
      try {
        console.log("Testing connection for vendor:", vendor);
        // Call the service and assume it returns a JSON object
        const response = await OAuthService.testConnection(vendor.vendor);
        // Check the status field in the response to determine success or failure.
        if (response.status === "pass") {
          this.addSuccessMessage(response.message);
        } else {
          this.addErrorMessage(response.message);
        }
      } catch (error) {
        // This catch block should handle network or unexpected errors.
        console.error("Test connection error:", error);
        this.addErrorMessage(
          `Test connection encountered an error for ${vendor.displayName}: ${error.message}`
        );
      }
    },
    handleAuthorizeSuccess(vendor: string) {
      const vendorIndex = this.vendors.findIndex((v) => v.vendor === vendor);
      if (vendorIndex !== -1) {
        Vue.set(this.vendors, vendorIndex, {
          ...this.vendors[vendorIndex],
          authorized: true,
        });
      }
      this.showModal = false;
      this.addSuccessMessage("Authorization successful.");
    },
    openSyncModal(vendor: Vendor) {
      this.selectedVendor = vendor;
      this.showSyncModal = true;
      this.syncingVendors.push(vendor.vendor);
    },
    closeSyncModal() {
      if (this.selectedVendor) {
        const index = this.syncingVendors.indexOf(this.selectedVendor.vendor);
        if (index > -1) {
          this.syncingVendors.splice(index, 1);
        }
      }
      this.showSyncModal = false;
      this.selectedVendor = null;
    },
    openSettingsModal(vendor: Vendor) {
      this.selectedVendor = vendor;

      if (vendor.vendor === "AUTHORIZE_NET") {
        const defaults = {
          apiLoginId: "",
          transactionKey: "",
          signatureKey: "",
          avsZipcode: false,
        };
        // Merge defaults with existing settings if any; otherwise, use defaults.
        this.presetSettings = vendor.settings
          ? { ...defaults, ...vendor.settings }
          : defaults;
      } else if (vendor.vendor === "GMAIL") {
        const defaults = {
          fromEmail: "",
        };
        this.presetSettings = vendor.settings
          ? { ...defaults, ...vendor.settings }
          : defaults;
      } else {
        // For other vendors, you can define other defaults or leave it empty.
        this.presetSettings = {};
      }

      this.showSettingsModal = true;
    },
    closeSettingsModal() {
      this.showSettingsModal = false;
      this.selectedVendor = null;
    },
    handleSettingsSave(updatedSettings: Record<string, any>) {
      // Use the selectedVendor's id (which holds the OAuth credentials ID) for the update.
      const credentialsId = this.selectedVendor.id;
      OAuthService.updateSettings(credentialsId, updatedSettings)
        .then(() => {
          // Update the local vendor.settings with the new values before closing the modal.
          this.selectedVendor.settings = { ...updatedSettings };

          this.addSuccessMessage(
            `Settings updated for ${this.selectedVendor.displayName}`,
          );

          console.log("updated settings: ", updatedSettings);

          // Now close the settings modal.
          this.closeSettingsModal();
        })
        .catch((error: any) => {
          console.error("Failed to update settings", error);
          this.addErrorMessage("Failed to update settings");
        });
    },
  },
});
